import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentNode } from '@apollo/client'

import { PaginationSource } from 'shared/hooks'
import { DATA_GRID_QUERY_PARAMS } from 'shared/hooks/use-data-grid-pagination'
import ConfirmDialog from '../ConfirmDialog'
import SingleSelectTable, {
  AddActionHandler,
  useSingleSelectActions,
  useSingleSelectTableSearch,
} from '../SingleSelectTable'
import { useAdminsTable } from './hooks/useAdminsTable'
import { useAdminsTableColumns } from './hooks/useAdminsTableColumns'
import { AdminRow, AdminsConnection, AdminsScopeVariables } from './types'

const EMAIL_SEARCH_QUERY_PARAM = 'email'

export const ADMINS_TABLE_QUERY_PARAMS = [
  EMAIL_SEARCH_QUERY_PARAM,
  ...Object.values(DATA_GRID_QUERY_PARAMS),
]

interface AddAdminDialogRenderProps {
  open: boolean
  onClose: () => void
  onSubmit: AddActionHandler
}

interface AdminsManagementTableProps<TData, TEdge> {
  adminsQuery: DocumentNode
  scopeVariables: AdminsScopeVariables
  selectAdmins: (data: TData) => AdminsConnection<TEdge> | null | undefined
  mapEdgeToRow: (edge: TEdge) => AdminRow
  paginationSource: PaginationSource
  onAddAdmin: (userId: string) => Promise<unknown>
  onRemoveAdmin: (userId: string) => Promise<unknown>
  addLabel: string
  isAddActionDisabled?: boolean
  renderRevokeMessage: (email?: string | null) => ReactNode
  renderAddDialog: (props: AddAdminDialogRenderProps) => ReactNode
}

const AdminsManagementTable = <TData, TEdge>({
  adminsQuery,
  scopeVariables,
  selectAdmins,
  mapEdgeToRow,
  paginationSource,
  onAddAdmin,
  onRemoveAdmin,
  addLabel,
  isAddActionDisabled,
  renderRevokeMessage,
  renderAddDialog,
}: AdminsManagementTableProps<TData, TEdge>) => {
  const { t } = useTranslation()
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const search = useSingleSelectTableSearch({
    queryParam: EMAIL_SEARCH_QUERY_PARAM,
  })

  const { rows, dataGridProps, isLoading, refetch } = useAdminsTable({
    adminsQuery,
    scopeVariables,
    selectAdmins,
    mapEdgeToRow,
    nameOrEmail: search.debouncedValue,
    paginationSource,
  })

  const {
    itemToRemove,
    isAddActionLoading,
    isRemoveActionLoading,
    cancelRemoveAction,
    requestRemoveAction,
    confirmRemoveAction,
    addAction,
  } = useSingleSelectActions({
    rows,
    refetch,
    onAddAction: onAddAdmin,
    onRemoveAction: onRemoveAdmin,
  })

  const columns = useAdminsTableColumns({
    search,
    onRemoveAdmin: requestRemoveAction,
    isRemoveAdminLoading: isRemoveActionLoading,
    emailLabel: t('email'),
  })

  return (
    <>
      <ConfirmDialog
        open={itemToRemove !== null}
        onConfirm={confirmRemoveAction}
        onCancel={cancelRemoveAction}
        isLoading={isRemoveActionLoading}
        message={itemToRemove ? renderRevokeMessage(itemToRemove.email) : null}
      />

      <SingleSelectTable
        rows={rows}
        columns={columns}
        dataGridProps={dataGridProps}
        isLoading={isLoading}
        isAddActionLoading={isAddActionLoading}
        isAddActionDisabled={isAddActionDisabled}
        addLabel={addLabel}
        onAddActionClick={() => setAddDialogOpen(true)}
        addDialog={renderAddDialog({
          open: addDialogOpen,
          onClose: () => setAddDialogOpen(false),
          onSubmit: addAction,
        })}
      />
    </>
  )
}

export default AdminsManagementTable
